import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { IncomeTestAdjustmentRequest } from '@interfaces/partner-income-tax/requests/partner-income-tax.request';
import {
  FindingDto,
  PartnerAssetDto,
  PartnerIncomeTaxPartnerDto,
} from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import { PartnerIncomeTaxStateService } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/partner-income-tax-state.service';
import {
  PartnerIncomeTaxSectionState,
  PartnerSectionKey,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/partner-income-tax-section.state';
import { valueForYear } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/partner-income-tax-format.util';
import { AssetEditDialogComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/asset-edit-dialog/asset-edit-dialog.component';
import { AssetsTableComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/assets-table/assets-table.component';
import { DataRoomDocumentsStore } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/data-room-documents/data-room-documents.store';
import { DebtsTableComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/debts-table/debts-table.component';
import { DismissFindingDialogComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/dismiss-finding-dialog/dismiss-finding-dialog.component';
import {
  DocumentsSectionComponent,
  SUPPORT_DOCS_NOTE,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/documents-section/documents-section.component';
import { FindingListComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/finding-list/finding-list.component';
import { IncomeTestPanelComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/income-test-panel/income-test-panel.component';
import { PartnerHeaderComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/partner-header/partner-header.component';
import { PartnerIndicatorsComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/partner-indicators/partner-indicators.component';
import {
  PatrimonialMutationTableComponent,
  mutationRowKind,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/patrimonial-mutation-table/patrimonial-mutation-table.component';
import {
  SectionTab,
  SectionTabsComponent,
  documentsTab,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/section-tabs/section-tabs.component';

/** Ponto "dívidas sobre o patrimônio": o nível dele dá a cor do indicador de dívidas. */
const DEBTS_OVER_NET_WORTH_POINT = 'IRS-D1';

/** Seções do sócio, na ordem das abas internas. */
const PARTNER_SECTIONS: readonly PartnerSectionKey[] = [
  'assets',
  'debts',
  'mutation',
  'income-test',
  'documents',
];

/**
 * Visão de um sócio no layout do protótipo 01: herói (conclusão e linha do tempo), 4 KPIs, pontos de atenção em
 * linhas e as abas internas (`?section=`) Bens e direitos, Dívidas e ônus, Mutação patrimonial, Teste de renda e
 * Documentos. As ações passam pelo state service.
 */
@Component({
  selector: 'app-pit-partner-detail',
  standalone: true,
  imports: [
    PartnerHeaderComponent,
    PartnerIndicatorsComponent,
    FindingListComponent,
    SectionTabsComponent,
    PatrimonialMutationTableComponent,
    IncomeTestPanelComponent,
    AssetsTableComponent,
    DebtsTableComponent,
    DocumentsSectionComponent,
    AssetEditDialogComponent,
    DismissFindingDialogComponent,
  ],
  templateUrl: './partner-detail.component.html',
  styleUrl: './partner-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerDetailComponent {
  @ViewChild('assetDialog') private assetDialog?: AssetEditDialogComponent;
  @ViewChild('dismissDialog') private dismissDialog?: DismissFindingDialogComponent;

  protected readonly state = inject(PartnerIncomeTaxStateService);
  protected readonly sections = inject(PartnerIncomeTaxSectionState);
  private readonly documents = inject(DataRoomDocumentsStore);

  readonly partner = input.required<PartnerIncomeTaxPartnerDto>();
  readonly policyDefined = input(true);

  protected readonly supportDocsNote = SUPPORT_DOCS_NOTE;

  /** Prévia do teste de renda enquanto o analista digita; volta a `undefined` a cada sócio novo. */
  protected readonly liveIncomeResult = signal<number | undefined>(undefined);

  protected readonly firstYear = computed(() => this.partner().window[0] ?? null);

  protected readonly section = computed(() => this.sections.resolve(PARTNER_SECTIONS));

  /** Abas internas com as contagens do protótipo ("Bens e direitos (21)", "Documentos · 2 a ler"). */
  protected readonly tabs = computed<SectionTab[]>(() => [
    { key: 'assets', title: 'Bens e direitos', count: this.partner().assets.length || undefined },
    { key: 'debts', title: 'Dívidas e ônus', count: this.partner().debts.length || undefined },
    { key: 'mutation', title: 'Mutação patrimonial' },
    { key: 'income-test', title: 'Teste de renda' },
    documentsTab(this.documents.total(), this.documents.pending().length),
  ]);

  protected readonly totalAssetsRow = computed(
    () =>
      this.partner().patrimonialMutation.find(
        row => mutationRowKind(row) === 'total' && /bens/i.test(row.groupLabel)
      ) ?? null
  );

  /**
   * Evolução do PL nos anos da janela, para a linha do KPI: a linha "Patrimônio líquido" da mutação patrimonial
   * (valores do backend). Sem ela, vazio e o KPI fica só com o número.
   */
  protected readonly netWorthSeries = computed(() => {
    const row = this.partner().patrimonialMutation.find(item =>
      /patrim[oô]nio l[ií]quido|net-worth/i.test(`${item.group} ${item.groupLabel}`)
    );
    if (!row) return [];
    return this.partner().window.map(year => ({ year, value: valueForYear(row.values, year) }));
  });

  /** Nível do ponto de dívidas sobre o patrimônio (IRS-D1), para a cor do indicador. */
  protected readonly debtsLevel = computed(
    () =>
      this.partner().findings.find(finding => finding.pointIds.includes(DEBTS_OVER_NET_WORTH_POINT))
        ?.level ?? null
  );

  constructor() {
    effect(() => {
      this.partner();
      this.liveIncomeResult.set(undefined);
    });
  }

  protected onAdjust(event: { returnId: string; request: IncomeTestAdjustmentRequest }): void {
    this.state.adjustIncomeTest(event.returnId, event.request);
  }

  protected onRestore(returnId: string): void {
    this.state.restoreIncomeTest(returnId);
  }

  protected onEditAsset(asset: PartnerAssetDto): void {
    this.assetDialog?.open(asset);
  }

  protected onDismiss(finding: FindingDto): void {
    this.dismissDialog?.open(finding);
  }

  protected onUndoDismiss(finding: FindingDto): void {
    this.state.undoDismissFinding(finding.pointIds[0]);
  }
}
